import React, { useId, useState } from 'react';
import { BarChart3, CheckCircle2, ChevronDown, Download, FileText, Lightbulb, MessageSquare, Sparkles, TrendingUp } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from './GlobalPhoebeChat';
type EvidenceItem = {
  label: string;
  value: string;
  detail: string;
  icon: React.ReactNode;
  accent: string;
  surface: string;
};
const EVIDENCE: EvidenceItem[] = [{
  label: 'Learning adoption',
  value: '80%',
  detail: 'of learners were active this month, up 20% from the prior period.',
  icon: <TrendingUp size={18} aria-hidden="true" data-id="element-1256" data-mp="1vztzbup2yj8k" />,
  accent: 'text-[#007558]',
  surface: 'bg-[#007558]/10'
}, {
  label: 'Learner sentiment',
  value: 'Predominantly 4–5/5',
  detail: 'Survey feedback describes the curriculum as practical, relevant, and easy to navigate.',
  icon: <CheckCircle2 size={18} aria-hidden="true" data-id="element-1257" data-mp="6l5ovz01vbd6" />,
  accent: 'text-[#30718D]',
  surface: 'bg-[#30718D]/10'
}, {
  label: 'Transfer to work',
  value: 'Immediate application',
  detail: 'Learners value leadership strategies and realistic workplace scenarios they can use right away.',
  icon: <Lightbulb size={18} aria-hidden="true" data-id="element-1258" data-mp="mrz79rhfy7el" />,
  accent: 'text-[#B77808]',
  surface: 'bg-[#F59E0B]/10'
}, {
  label: 'Strategic relevance',
  value: 'AI + healthcare',
  detail: 'AI-focused learning was timely and valuable for healthcare professionals.',
  icon: <BarChart3 size={18} aria-hidden="true" data-id="element-1259" data-mp="1ai385r38vt1w" />,
  accent: 'text-[#DB3725]',
  surface: 'bg-[#DB3725]/10'
}];
const MEASUREMENT_STEPS = [['Adoption', 'Active participation and completion by cohort'], ['Experience', 'Satisfaction, relevance, usability, and confidence to apply learning'], ['Impact', 'Manager-confirmed application and one business outcome with a baseline']];
export function ExecutiveValueReport() {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();
  const downloadExecutiveBrief = () => {
    const report = `Kaiser ROI Report\n\nExecutive summary\nLearner feedback indicates that the professional development curriculum is delivering meaningful value: learners consistently describe it as practical, relevant, engaging, and easy to navigate. Financial ROI should be calculated after program cost, learner time, and the chosen business outcome are added.\n\nEvidence of value\n• 80% monthly learner participation, up 20% from the prior period.\n• Survey ratings were predominantly 4–5 out of 5.\n• Learners value immediately applicable leadership strategies and realistic workplace scenarios.\n• AI-focused learning is timely and valuable for healthcare professionals.\n\nRecommended next measures\n1. Track completion and active participation by cohort.\n2. Add manager confirmation of on-the-job application.\n3. Connect program cost, learner time, and one business outcome to calculate financial ROI.\n\nOpportunities to improve outcomes\nHelp more learners finish and apply their learning through protected learning time, manager encouragement, and targeted reminders for learners with modules remaining.`;
    const blob = new Blob([report], {
      type: 'text/plain;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kaiser-roi-report.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const openPhoebeForRoi = () => {
    openPhoebeChat({
      prompt: 'Help me tailor the ROI Report.',
      response: 'I can turn the current evidence into a leadership-ready narrative. Your strongest proof points are growing learner participation, consistently positive feedback, immediately applicable leadership strategies, and timely AI learning for healthcare teams.\n\nTo calculate a financial ROI, add three organization-specific inputs:\n• Total program cost\n• Learner and manager time invested\n• One measurable business outcome, such as reduced time to proficiency, retention, quality, or manager effectiveness\n\nWhich outcome is most important to your leadership team?',
      suggestedQuestions: ['Draft a leadership summary', 'Choose an ROI outcome', 'Define the cost inputs'],
      suggestionResponses: {
        'Draft a leadership summary': 'Here is a concise leadership message: “Learner feedback confirms the curriculum is relevant, practical, and easy to use. Participation has reached 80%, up 20% from the prior period, and employees are applying leadership and AI skills to real workplace situations. The next opportunity is helping more learners finish — through protected learning time, manager encouragement, and reminders for those with modules remaining.”',
        'Choose an ROI outcome': 'Choose the outcome that matters most to this audience: manager effectiveness, faster readiness for new responsibilities, employee retention, quality and safety, or time saved through improved AI fluency. Start with one outcome that already has a baseline measure.',
        'Define the cost inputs': 'Use the platform and content cost, learner learning hours multiplied by loaded hourly rate, manager or facilitator time, and any implementation support. Compare those inputs with the measurable benefit from the business outcome you selected.'
      }
    });
  };
  return <section aria-labelledby="executive-value-title" className="mb-8 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1260" data-mp="20508nkoqibqj">
      <div className="flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start" data-id="element-1261" data-mp="1q9yjiougl9ny">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-[#30718D] text-white shadow-sm" data-id="element-1262" data-mp="1lb5elkt8mqze">
          <FileText size={24} aria-hidden="true" data-id="element-1263" data-mp="3kmodf6ktuivc" />
        </span>

        <div className="min-w-0 flex-1" data-id="element-1264" data-mp="3fnv8w3niq27b">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1265" data-mp="3fowmofvx52sq">
            <div className="min-w-0" data-id="element-1266" data-mp="1dxmnz8ji93sq">
              <div className="flex items-center gap-2" data-id="element-1267" data-mp="1ipe56sgrtx67">
                <h2 id="executive-value-title" className="text-lg font-bold text-gray-900" data-id="element-1268" data-mp="1iqyxscjiz61f">
                  ROI Report
                </h2>
                <button type="button" onClick={openPhoebeForRoi} aria-label="Tailor the ROI Report with Phoebe" title="Tailor with Phoebe" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#30718D]/30 bg-white text-[#30718D] shadow-sm transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-1269" data-mp="190e5f6ug2xqy">
                  <Sparkles size={15} aria-hidden="true" data-id="element-1270" data-mp="c80roapwh6n3" />
                </button>
              </div>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-gray-600" data-id="element-1271" data-mp="gcqj9ndizz27">
                Connect learner adoption and end-of-course feedback to a clear
                executive case for continuing—and expanding—your learning investment.
              </p>
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1272" data-mp="ioj2izyi7kkh">
              <button type="button" onClick={() => setIsExpanded(expanded => !expanded)} aria-controls={contentId} aria-expanded={isExpanded} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5 hover:text-[#30718D] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 sm:w-auto" data-id="element-1273" data-mp="draiettsp6gl">
                {isExpanded ? 'Hide details' : 'View details'}
                <ChevronDown size={16} aria-hidden="true" className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1274" data-mp="2yevq8g1azd4s" />
              </button>
              <button type="button" onClick={downloadExecutiveBrief} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#DB3725] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#C02F1F] focus:outline-none focus:ring-2 focus:ring-[#DB3725] focus:ring-offset-2 sm:w-auto" data-id="element-1275" data-mp="2mw305vssx85b">
                <Download size={14} aria-hidden="true" data-id="element-1276" data-mp="30t9i5o5pb2th" />
                Download Report
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-4 text-xs font-medium text-gray-500 sm:flex-row sm:flex-wrap sm:gap-x-5 sm:gap-y-2" data-id="element-1277" data-mp="3akdush7wmbl5">
            <span className="inline-flex items-center gap-1.5" data-id="element-1278" data-mp="3jm3nrrhfpil1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#007558]" data-id="element-1279" data-mp="3jmddrf2hbpqq" />
              80% active participation
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-1280" data-mp="12zngbpejj6o0">
              <span className="h-1.5 w-1.5 rounded-full bg-[#30718D]" data-id="element-1281" data-mp="12y4367o6lbqg" />
              Predominantly 4–5/5 feedback
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-1282" data-mp="tyz32wwz1ac6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#B77808]" data-id="element-1283" data-mp="u0k0cuao77dq" />
              Financial ROI inputs required
            </span>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-1284" data-mp="1mopid9acai9v">
        {isExpanded && <motion.div id={contentId} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} className="overflow-hidden border-t border-gray-100" data-id="element-1285" data-mp="1hwo9zygalbp4">
            <div className="p-5 sm:p-6" data-id="element-1286" data-mp="25lh8o2oatxnf">
              <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 lg:flex-row lg:items-start lg:justify-between" data-id="element-1287" data-mp="25knzyw5s4y2n">
                <div className="min-w-0 max-w-3xl" data-id="element-1288" data-mp="31j2c2tsq09aq">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#30718D]" data-id="element-1289" data-mp="31kddwo2lv6z7">
                    Executive value story
                  </p>
                  <h3 className="mt-1 text-xl font-bold leading-tight text-gray-900" data-id="element-1290" data-mp="3bggqyoyzwq0x">
                    The curriculum is earning learner trust and building relevant capability.
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1291" data-mp="3ki6g1o5encvj">
                    Kaiser learners see the curriculum as practical and ready to
                    apply, particularly for leadership and AI-focused healthcare
                    development. The strongest next step is to help more learners
                    finish and apply it — through protected learning time, manager
                    encouragement, and reminders for those with modules remaining.
                  </p>
                </div>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap lg:justify-end" data-id="element-1292" data-mp="qotpnt8evref">
                  <button type="button" onClick={openPhoebeForRoi} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#30718D]/30 bg-white px-3 py-2 text-sm font-semibold text-[#30718D] transition-colors hover:bg-[#30718D]/5 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 sm:w-auto" data-id="element-1293" data-mp="vlal78hxx81j">
                    <MessageSquare size={16} aria-hidden="true" data-id="element-1294" data-mp="3u5aglfkhlhp7" />
                    Tailor with Phoebe
                  </button>
                </div>
              </div>

              <section aria-labelledby="evidence-title" className="mt-6" data-id="element-1295" data-mp="2wmwf2xzg0j8">
                <h4 id="evidence-title" className="text-sm font-bold text-gray-900" data-id="element-1296" data-mp="3l4ugmg2ycsp3">
                  Evidence of value
                </h4>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(13.5rem,1fr))]" data-id="element-1297" data-mp="1vn719c4p4j85">
                  {EVIDENCE.map(item => <article key={item.label} className="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-4" data-id="element-1298" data-mp="1quxgeaifu9cq">
                      <div className="flex items-start gap-3" data-id="element-1299" data-mp="1hwtvngfa1glz">
                        <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${item.surface} ${item.accent}`} data-id="element-1300" data-mp="2elt00kcqpy8w">
                          {item.icon}
                        </span>
                        <div className="min-w-0" data-id="element-1301" data-mp="25jjt04gokn09">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-1302" data-mp="20mtevmofs1hh">
                            {item.label}
                          </p>
                          <p className="mt-1 break-words text-sm font-bold leading-snug text-gray-900" data-id="element-1303" data-mp="u3v7zimzly23">
                            {item.value}
                          </p>
                        </div>
                      </div>
                      <p className="mt-3 break-words text-sm leading-relaxed text-gray-600" data-id="element-1304" data-mp="35srw46ebt70b">
                        {item.detail}
                      </p>
                    </article>)}
                </div>
              </section>

              <section aria-labelledby="measurement-title" className="mt-6 rounded-lg border border-[#30718D]/20 bg-[#EAF4F7] p-4 sm:p-5" data-id="element-1305" data-mp="3enzi59xtf71k">
                <div className="flex flex-col gap-1" data-id="element-1306" data-mp="3oi7xsx1qz5ib">
                  <h4 id="measurement-title" className="text-sm font-bold text-gray-900" data-id="element-1307" data-mp="3tima4radt1m5">
                    Complete the financial ROI case
                  </h4>
                  <p className="max-w-4xl text-sm leading-relaxed text-gray-600" data-id="element-1308" data-mp="1d0jm7d7d11p">
                    Financial return is not estimated here because cost and business
                    outcome assumptions have not yet been provided. Use the same
                    reporting cycle to connect this evidence to one measurable outcome.
                  </p>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(13.5rem,1fr))]" data-id="element-1309" data-mp="13uogtiaoqdm6">
                  {MEASUREMENT_STEPS.map(([label, detail], index) => <div key={label} className="min-w-0 border-l-2 border-[#7AB6CC] pl-3" data-id="element-1310" data-mp="1cw5u6swpk176" data-mpi={index}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#30718D]" data-id="element-1311" data-mp="p6tow44i4da5">
                        {index + 1}. {label}
                      </p>
                      <p className="mt-1 break-words text-sm leading-relaxed text-gray-700" data-id="element-1312" data-mp="ywd8xd3yad6l">
                        {detail}
                      </p>
                    </div>)}
                </div>
              </section>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}